import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, CountryOptions } from '../lib/utils';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Pencil, Trash2, Building2, Search, Loader2, Users, Filter, X, Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { SortableHeader } from '../components/SortableHeader';

const Companies = () => {
  usePageTitle('Companies');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [trainingCenters, setTrainingCenters] = useState([]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 50;
  
  // Filter states
  const [countryFilter, setCountryFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [filterOptions, setFilterOptions] = useState({ countries: [], cities: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Sort state
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    telephone: '',
    domain: '',
    city: '',
    country: '',
    address: '',
    fax: '',
    training_center_id: '',
    category: '',
    primary_contact: ''
  });

  const fetchData = async (page = 1, search = '', sortByArg = sortBy, sortOrderArg = sortOrder) => {
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (search) params.append('search', search);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (countryFilter !== 'all') params.append('country', countryFilter);
      if (cityFilter !== 'all') params.append('city', cityFilter);
      if (sortByArg) {
        params.append('sort_by', sortByArg);
        params.append('sort_order', sortOrderArg);
      }
      
      const [companiesRes, centersRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/companies?${params}`),
        axios.get(`${API_URL}/training-centers`),
        axios.get(`${API_URL}/company-categories`)
      ]);
      
      // Handle paginated response
      const companiesData = companiesRes.data.items || companiesRes.data;
      setCompanies(companiesData);
      setTotalPages(companiesRes.data.total_pages || 1);
      setTotalItems(companiesRes.data.total || companiesData.length);
      setCurrentPage(companiesRes.data.page || 1);
      
      setTrainingCenters(centersRes.data);
      setCategoryOptions(categoriesRes.data.map(cat => ({ value: cat.name, label: cat.name })));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, '');
    // Country/city dropdown options come from the whole DB, not the current page
    axios.get(`${API_URL}/companies/filter-options`)
      .then(res => setFilterOptions(res.data))
      .catch(() => {});
  }, []);

  // Handle ?edit= query parameter from CompanyDetail page (legacy entry point)
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId) return;
    // Edit now lives on a dedicated page — redirect there in the same tab.
    setSearchParams({});
    navigate(`/companies/${editId}/edit`, { replace: true });
  }, [searchParams, navigate, setSearchParams]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(1, searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, categoryFilter, countryFilter, cityFilter]);

  const handlePageChange = (newPage) => {
    setLoading(true);
    fetchData(newPage, searchTerm);
  };

  const handleSort = (field) => {
    let nextBy = field;
    let nextOrder = 'asc';
    if (sortBy === field) {
      if (sortOrder === 'asc') nextOrder = 'desc';
      else { nextBy = null; nextOrder = 'asc'; }
    }
    setSortBy(nextBy);
    setSortOrder(nextOrder);
    setLoading(true);
    fetchData(1, searchTerm, nextBy, nextOrder);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      telephone: '',
      domain: '',
      city: '',
      country: '',
      address: '',
      fax: '',
      training_center_id: '',
      category: '',
      primary_contact: ''
    });
  };

  // Filter dropdown options come from the whole DB (see filter-options fetch)
  const uniqueCountries = filterOptions.countries;
  const uniqueCities = filterOptions.cities;

  // Companies are filtered server-side (search, category, country, city)
  const filteredCompanies = companies;

  // Count active filters
  const activeFilterCount = [
    countryFilter !== 'all',
    cityFilter !== 'all',
    categoryFilter !== 'all'
  ].filter(Boolean).length;

  const clearFilters = () => {
    setCountryFilter('all');
    setCityFilter('all');
    setCategoryFilter('all');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Please enter a company name');
      return;
    }

    setSaving(true);
    
    try {
      const payload = { ...formData };
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') payload[key] = null;
      });
      payload.name = formData.name; // Ensure name is not null
      
      if (editingCompany) {
        await axios.put(`${API_URL}/companies/${editingCompany.id}`, payload);
        toast.success('Company updated successfully');
      } else {
        await axios.post(`${API_URL}/companies`, payload);
        toast.success('Company created successfully');
      }
      
      setDialogOpen(false);
      setEditingCompany(null);
      resetForm();
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to save company';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      email: company.email || '',
      telephone: company.telephone || '',
      domain: company.domain || '',
      city: company.city || '',
      country: company.country || '',
      address: company.address || '',
      fax: company.fax || '',
      training_center_id: company.training_center_id || '',
      category: company.category || '',
      primary_contact: company.primary_contact || ''
    });
    setDialogOpen(true);
  };

  const getTrainingCenterName = (centerId) => {
    const center = trainingCenters.find(c => c.id === centerId);
    return center?.name || '-';
  };

  const handleDelete = async (company) => {
    setDeleteConfirm(company);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const company = deleteConfirm;
    setDeleteConfirm(null);
    
    try {
      await axios.delete(`${API_URL}/companies/${company.id}`);
      toast.success('Company deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  // Import handlers
  const handleImportClick = () => {
    setImportFile(null);
    setImportResult(null);
    setImportDialogOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a file');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API_URL}/companies/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} companies`);
        fetchData(); // Refresh the list
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Import failed';
      toast.error(detail);
      setImportResult({ success: false, errors: [detail], imported: 0, skipped: 0, total_rows: 0 });
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-96 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="companies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Companies
          </h1>
          <p className="text-slate-500 mt-1">
            Manage client companies and their details
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={handleImportClick}
            data-testid="import-companies-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </Button>
          <Button 
            className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
            onClick={() => {
              setEditingCompany(null);
              resetForm();
              setDialogOpen(true);
            }}
            data-testid="add-company-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Company
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="company-search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={activeFilterCount > 0 ? 'border-[#2C9DE1] text-[#2C9DE1]' : ''}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-2 bg-[#2C9DE1] text-white">{activeFilterCount}</Badge>
              )}
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="border-t pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-slate-900">Filter Companies</h3>
                {activeFilterCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-1" />
                    Clear all
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Country Filter */}
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Select value={countryFilter} onValueChange={setCountryFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {uniqueCountries.map((country) => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* City Filter */}
                <div className="space-y-2">
                  <Label>City</Label>
                  <Select value={cityFilter} onValueChange={setCityFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Cities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cities</SelectItem>
                      {uniqueCities.map((city) => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Category Filter */}
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categoryOptions.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Results count */}
              <div className="text-sm text-slate-500">
                Showing {companies.length} of {totalItems} matching companies
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Companies Table */}
      <Card>
        <CardContent className="p-0">
          {filteredCompanies.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No companies found</h3>
              <p className="text-slate-500 mt-1">
                {searchTerm || activeFilterCount > 0 ? 'Try adjusting your filters' : 'Get started by adding your first company'}
              </p>
              {activeFilterCount > 0 && (
                <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <SortableHeader field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-company-name">Company Name</SortableHeader>
                  <SortableHeader field="category" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-company-category">Category</SortableHeader>
                  <SortableHeader field="primary_contact" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-company-contact">Primary Contact</SortableHeader>
                  <SortableHeader field="email" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-company-email">Email</SortableHeader>
                  <SortableHeader field="telephone" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-company-tel">Telephone</SortableHeader>
                  <SortableHeader field="city" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} testId="sort-company-location">Location</SortableHeader>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompanies.map((company) => (
                  <TableRow key={company.id} className="hover:bg-slate-50/50" data-testid={`company-row-${company.id}`}>
                    <TableCell 
                      className="font-medium text-[#2C9DE1] cursor-pointer hover:underline"
                      onClick={() => navigate(`/companies/${company.id}`)}
                    >
                      {company.name}
                    </TableCell>
                    <TableCell>
                      {company.category ? (
                        <Badge variant="outline">{company.category}</Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{company.primary_contact || '-'}</TableCell>
                    <TableCell>{company.email || '-'}</TableCell>
                    <TableCell>{company.telephone || '-'}</TableCell>
                    <TableCell>
                      {company.city && company.country 
                        ? `${company.city}, ${company.country}` 
                        : company.city || company.country || '-'}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`company-actions-${company.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/students?company=${company.id}`)}>
                            <Users className="w-4 h-4 mr-2" />
                            View Employees
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => window.open(`/companies/${company.id}/edit`, '_blank', 'noopener,noreferrer')}
                            data-testid={`company-edit-${company.id}`}
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(company)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-slate-500">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalItems)} of {totalItems} companies
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingCompany ? 'Edit Company' : 'Add New Company'}
            </DialogTitle>
            <DialogDescription>
              {editingCompany ? 'Update company details' : 'Add a new client company'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="form-section-header">Company Information</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Company Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Company Ltd"
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category || "none"}
                    onValueChange={(value) => setFormData({ ...formData, category: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="company-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No category</SelectItem>
                      {categoryOptions.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="info@company.com"
                    data-testid="company-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Input
                    id="domain"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    placeholder="company.com"
                    data-testid="company-domain-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="telephone">Telephone</Label>
                  <Input
                    id="telephone"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    placeholder="+357 22 123456"
                    data-testid="company-telephone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fax">Fax</Label>
                  <Input
                    id="fax"
                    value={formData.fax}
                    onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                    placeholder="+357 22 123457"
                    data-testid="company-fax-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="primary_contact">Primary Contact</Label>
                <Input
                  id="primary_contact"
                  value={formData.primary_contact}
                  onChange={(e) => setFormData({ ...formData, primary_contact: e.target.value })}
                  placeholder="John Smith"
                  maxLength={200}
                  data-testid="company-primary-contact-input"
                />
              </div>
            </div>

            {/* Location */}
            <div className="space-y-4">
              <h3 className="form-section-header">Location</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select
                    value={formData.country || "none"}
                    onValueChange={(value) => setFormData({ ...formData, country: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="company-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select country</SelectItem>
                      {CountryOptions.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Nicosia"
                    data-testid="company-city-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main Street"
                  data-testid="company-address-input"
                />
              </div>
            </div>

            {/* Training Center Assignment */}
            <div className="space-y-4">
              <h3 className="form-section-header">Training Center</h3>
              
              <div className="space-y-2">
                <Label htmlFor="training_center">Assigned Training Center</Label>
                <Select
                  value={formData.training_center_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, training_center_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="company-trainingcenter-select">
                    <SelectValue placeholder="Select a training center" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No training center</SelectItem>
                    {trainingCenters.map((center) => (
                      <SelectItem key={center.id} value={center.id}>
                        {center.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
                disabled={saving}
                data-testid="company-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingCompany ? 'Update Company' : 'Create Company'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={true} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Delete Company
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 mt-4">
              <Button
                variant="outline"
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white border-0"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Import Companies from CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to import multiple companies at once
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File Upload */}
            <div className="space-y-2">
              <Label>CSV File</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-[#2C9DE1] transition-colors">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                  data-testid="csv-file-input"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <FileText className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                  {importFile ? (
                    <p className="text-sm font-medium text-slate-900">{importFile.name}</p>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-slate-900">Click to upload CSV</p>
                      <p className="text-xs text-slate-500 mt-1">or drag and drop</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Format Info */}
            <div className="bg-slate-50 rounded-lg p-4 text-sm">
              <h4 className="font-medium text-slate-900 mb-2">Expected CSV Format:</h4>
              <ul className="text-slate-600 space-y-1">
                <li><strong>company</strong> or <strong>name</strong> (required)</li>
                <li>email, telephone, fax, city, country, address (optional)</li>
                <li>category, primary_contact (optional)</li>
              </ul>
              <p className="text-xs text-slate-500 mt-2">
                Duplicate company names will be skipped.
              </p>
            </div>

            {/* Import Result */}
            {importResult && (
              <div className={`rounded-lg p-4 ${importResult.success ? 'bg-green-50' : 'bg-amber-50'}`}>
                <div className="flex items-start gap-3">
                  {importResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h4 className={`font-medium ${importResult.success ? 'text-green-900' : 'text-amber-900'}`}>
                      Import {importResult.success ? 'Complete' : 'Completed with Warnings'}
                    </h4>
                    <div className="text-sm mt-1 space-y-1">
                      <p><strong>{importResult.imported}</strong> companies imported</p>
                      {importResult.skipped > 0 && (
                        <p><strong>{importResult.skipped}</strong> duplicates skipped</p>
                      )}
                      <p className="text-slate-500">Total rows processed: {importResult.total_rows}</p>
                    </div>
                    {importResult.errors && importResult.errors.length > 0 && (
                      <div className="mt-2 max-h-32 overflow-y-auto">
                        <p className="text-xs font-medium text-amber-800">Errors:</p>
                        <ul className="text-xs text-amber-700 list-disc list-inside">
                          {importResult.errors.slice(0, 10).map((error, i) => (
                            <li key={i}>{error}</li>
                          ))}
                          {importResult.errors.length > 10 && (
                            <li>...and {importResult.errors.length - 10} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              {importResult ? 'Close' : 'Cancel'}
            </Button>
            {!importResult && (
              <Button
                onClick={handleImport}
                disabled={!importFile || importing}
                className="bg-[#2C9DE1] hover:bg-[#1A7BB8]"
                data-testid="import-submit-btn"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Companies;
